import React, { useState } from 'react';
import { HiX, HiOutlineUser, HiOutlineMail, HiOutlineKey, HiOutlineExclamation } from 'react-icons/hi';
import { authAPI, watchlistAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useDevice } from '../hooks';

function ProfileEditModal({ user, onClose, onUpdate }) {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    newPassword: '',
    confirmPassword: '',
    preferredLanguage: user?.preferred_language || 'pt-BR',
    systemLanguage: user?.system_language || 'pt-BR',
  });
  const [loading, setLoading] = useState(false);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Check if email is being changed
    if (name === 'email' && value !== user?.email) {
      setIsChangingEmail(true);
    } else if (name === 'email') {
      setIsChangingEmail(false);
    }
  };

  const handleSaveClick = (e) => {
    e.preventDefault();

    // Check if email is being changed
    if (formData.email !== user?.email) {
      setShowEmailWarning(true);
      return;
    }

    handleSubmit(e);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    try {
      // Validate passwords if trying to change password
      if (formData.newPassword || formData.confirmPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error(t('profile.passwordsDontMatch'));
          setLoading(false);
          return;
        }
        if (formData.newPassword.length < 6) {
          toast.error(t('profile.passwordTooShort'));
          setLoading(false);
          return;
        }
      }

      // Prepare update data
      const updateData = {};

      if (formData.username !== user?.username) {
        updateData.username = formData.username;
      }

      if (formData.email !== user?.email) {
        updateData.email = formData.email;
      }

      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }

      if (formData.preferredLanguage !== user?.preferred_language) {
        updateData.preferred_language = formData.preferredLanguage;
      }

      if (formData.systemLanguage !== user?.system_language) {
        updateData.system_language = formData.systemLanguage;
      }

      // Only call API if there are changes
      if (Object.keys(updateData).length === 0) {
        toast.success(t('profile.noChanges'));
        setLoading(false);
        return;
      }

      await authAPI.updateProfile(updateData);

      // If system language was changed, update i18n and localStorage
      const systemLanguageChanged = updateData.system_language !== undefined;
      if (systemLanguageChanged) {
        i18next.changeLanguage(formData.systemLanguage);
        localStorage.setItem('systemLanguage', formData.systemLanguage);
      }

      // If content language was changed, sync watchlist data
      const languageChanged = updateData.preferred_language !== undefined;
      if (languageChanged) {
        console.log('=== LANGUAGE CHANGE DETECTED ===');
        console.log('Old language:', user?.preferred_language);
        console.log('New language:', formData.preferredLanguage);

        toast.success(t('profile.profileUpdatedSyncing'));
        try {
          console.log('Starting language sync...');
          const syncResponse = await watchlistAPI.syncLanguage();
          console.log('Language sync response:', syncResponse);
          console.log('Sync stats:', {
            updated: syncResponse.data?.updated,
            failed: syncResponse.data?.failed,
            total: syncResponse.data?.total
          });

          if (syncResponse.data?.failed > 0) {
            toast.error(`${t('profile.contentSynced')} - ${syncResponse.data.failed} ${t('common.error')}`);
          } else {
            toast.success(t('profile.contentSynced'));
          }

          // Refresh user data
          if (onUpdate) {
            await onUpdate();
          }

          // Close modal and reload page to show updated content
          onClose();

          // Reload page after a short delay to show the success message
          setTimeout(() => {
            console.log('Reloading page to show updated content...');
            window.location.reload();
          }, 1000);
          return; // Exit early to prevent further execution
        } catch (syncError) {
          console.error('Error syncing watchlist:', syncError);
          console.error('Sync error details:', syncError.response?.data);
          const syncErrorMsg = syncError.response?.data?.detail || t('profile.syncError');
          toast.error(syncErrorMsg);
          setLoading(false);
          return; // Don't close modal or continue if sync fails
        }
      } else {
        toast.success(isChangingEmail
          ? t('profile.emailChangeWarning')
          : t('profile.profileUpdated')
        );
      }

      // Refresh user data
      if (onUpdate) {
        await onUpdate();
      }

      // Close modal immediately
      onClose();

      // If email was changed, wait before logout
      if (isChangingEmail) {
        setTimeout(() => {
          // User will be logged out automatically by backend
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('profile.editProfile')}</h2>
          <button className="modal-close" onClick={onClose}>
            <HiX />
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px 24px' }}>
          <form>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ marginBottom: '5px', fontSize: '13px' }}>{t('common.username')}</label>
              <div style={{ position: 'relative' }}>
                <HiOutlineUser
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    fontSize: '18px'
                  }}
                />
                <input
                  type="text"
                  name="username"
                  className="form-input"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t('profile.enterUsername')}
                  style={{ paddingLeft: '40px', height: '36px', fontSize: '14px' }}
                  required
                />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ marginBottom: '5px', fontSize: '13px' }}>{t('common.email')}</label>
              <div style={{ position: 'relative' }}>
                <HiOutlineMail
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    fontSize: '18px'
                  }}
                />
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('profile.enterEmail')}
                  style={{ paddingLeft: '40px', height: '36px', fontSize: '14px' }}
                  required
                />
              </div>
            </div>

            <div style={{ marginTop: '12px', marginBottom: '10px' }}>
              <h3
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: 'var(--text-primary)',
                }}
              >
                {t('profile.languagePreference')}
              </h3>
              <div className="language-toggle-container">
                {/* Background slider */}
                <div
                  className={`language-toggle-slider ${formData.preferredLanguage === 'pt-BR' ? 'active-left' : 'active-right'}`}
                />

                {/* PT-BR Option */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, preferredLanguage: 'pt-BR' })}
                  style={{
                    flex: 1,
                    padding: '7px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '7px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: formData.preferredLanguage === 'pt-BR' ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'color 0.3s',
                    zIndex: 2,
                    position: 'relative'
                  }}
                >
                  <img
                    src="https://flagcdn.com/w40/br.png"
                    alt="Brasil"
                    style={{
                      width: '24px',
                      height: '16px',
                      objectFit: 'cover',
                      borderRadius: '2px'
                    }}
                  />
                  PT-BR
                </button>

                {/* EN Option */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, preferredLanguage: 'en' })}
                  style={{
                    flex: 1,
                    padding: '7px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '7px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: formData.preferredLanguage === 'en' ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'color 0.3s',
                    zIndex: 2,
                    position: 'relative'
                  }}
                >
                  <img
                    src="https://flagcdn.com/w40/us.png"
                    alt="USA"
                    style={{
                      width: '24px',
                      height: '16px',
                      objectFit: 'cover',
                      borderRadius: '2px'
                    }}
                  />
                  EN
                </button>
              </div>
            </div>

            <div style={{ marginTop: '12px', marginBottom: '10px' }}>
              <h3
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: 'var(--text-primary)',
                }}
              >
                {t('profile.changePassword')}
              </h3>

              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label className="form-label" style={{ marginBottom: '5px', fontSize: '13px' }}>{t('profile.newPassword')}</label>
                <div style={{ position: 'relative' }}>
                  <HiOutlineKey
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                      fontSize: '18px'
                    }}
                  />
                  <input
                    type="password"
                    name="newPassword"
                    className="form-input"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder={t('profile.enterNewPassword')}
                    style={{ paddingLeft: '40px', height: '36px', fontSize: '14px' }}
                    minLength={6}
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label className="form-label" style={{ marginBottom: '5px', fontSize: '13px' }}>{t('profile.confirmPassword')}</label>
                <div style={{ position: 'relative' }}>
                  <HiOutlineKey
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                      fontSize: '18px'
                    }}
                  />
                  <input
                    type="password"
                    name="confirmPassword"
                    className="form-input"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder={t('profile.confirmNewPassword')}
                    style={{ paddingLeft: '40px', height: '36px', fontSize: '14px' }}
                    minLength={6}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '12px', marginBottom: '10px' }}>
              <h3
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: 'var(--text-primary)',
                }}
              >
                {t('profile.systemLanguage')}
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px'
              }}>
                {/* PT-BR Option */}
                <label
                  className="system-language-option"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: '2px solid',
                    borderColor: formData.systemLanguage === 'pt-BR' ? 'var(--primary)' : undefined,
                    backgroundColor: formData.systemLanguage === 'pt-BR' ? 'rgba(124, 58, 237, 0.05)' : 'transparent',
                    transition: 'all 0.2s ease',
                    flex: 1
                  }}
                >
                  {/* Custom Radio Button */}
                  <div
                    className="system-language-radio"
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: formData.systemLanguage === 'pt-BR' ? 'var(--primary)' : undefined,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {formData.systemLanguage === 'pt-BR' && (
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--primary)'
                        }}
                      />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="systemLanguage"
                    value="pt-BR"
                    checked={formData.systemLanguage === 'pt-BR'}
                    onChange={() => setFormData({ ...formData, systemLanguage: 'pt-BR' })}
                    style={{ display: 'none' }}
                  />
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: formData.systemLanguage === 'pt-BR' ? '600' : '500',
                      color: formData.systemLanguage === 'pt-BR' ? 'var(--primary)' : 'var(--text-primary)'
                    }}
                  >
                    PT-BR
                  </span>
                </label>

                {/* EN-US Option */}
                <label
                  className="system-language-option"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: '2px solid',
                    borderColor: formData.systemLanguage === 'en-US' ? 'var(--primary)' : undefined,
                    backgroundColor: formData.systemLanguage === 'en-US' ? 'rgba(124, 58, 237, 0.05)' : 'transparent',
                    transition: 'all 0.2s ease',
                    flex: 1
                  }}
                >
                  {/* Custom Radio Button */}
                  <div
                    className="system-language-radio"
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: formData.systemLanguage === 'en-US' ? 'var(--primary)' : undefined,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {formData.systemLanguage === 'en-US' && (
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--primary)'
                        }}
                      />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="systemLanguage"
                    value="en-US"
                    checked={formData.systemLanguage === 'en-US'}
                    onChange={() => setFormData({ ...formData, systemLanguage: 'en-US' })}
                    style={{ display: 'none' }}
                  />
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: formData.systemLanguage === 'en-US' ? '600' : '500',
                      color: formData.systemLanguage === 'en-US' ? 'var(--primary)' : 'var(--text-primary)'
                    }}
                  >
                    EN-US
                  </span>
                </label>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '12px 24px', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveClick}
                disabled={loading}
              >
                {loading ? t('profile.saving') : t('profile.saveChanges')}
              </button>
            </div>

            {/* Espaço em branco para permitir scroll em baixas resoluções (apenas mobile) */}
            {isMobile && <div style={{ height: '80px' }}></div>}
          </form>
        </div>
      </div>

      {/* Email Change Warning Modal */}
      {showEmailWarning && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>{t('emailWarning.attention')}</h2>
            </div>

            <div className="modal-body" style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                <HiOutlineExclamation size={48} style={{ color: 'var(--warning)' }} />
              </div>
              <p style={{ fontSize: '15px', lineHeight: '1.6', color: 'var(--text-secondary)' }}
                 dangerouslySetInnerHTML={{ __html: t('emailWarning.message') }} />
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '16px' }}
                 dangerouslySetInnerHTML={{ __html: t('emailWarning.newEmailInfo', { email: formData.email }) }} />
            </div>

            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEmailWarning(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => {
                  setShowEmailWarning(false);
                  handleSubmit(e);
                }}
              >
                {t('emailWarning.understood')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileEditModal;
